<template>
	<div class="brick">
		<div class="list_top_box">
			<div class="left_option">
				<em class="lt_text">Ship/PickUp Date</em>
				<span class="mgr_15"> <CustomDatePicker v-model="from_date" /> ~ <CustomDatePicker v-model="to_date" /> </span>

				<em class="lt_text">Delivery Type</em>
				<span class="int_type_no_border mgr_15">
					<label>
						<select v-model="delivery_type_code" class="tbl_select">
							<option value="">All</option>
							<option v-for="dt in deliveryTypes" :key="dt.code" :value="dt.code">{{ dt.name }}</option>
						</select>
					</label>
				</span>

				<span class="int_type_no_border mgr_15">
					<input type="checkbox" class="int_type" id="group_by_route" v-model="group_by_route" />
					<label class="lt_text" for="group_by_route">Group By Route</label>
				</span>

				<a href="javascript:void(0);" class="search_bt" @click="search()">Search</a>
				<a href="javascript:void(0);" class="gray_bt mgr_15" @click="reset()">Reset</a>
				<span v-if="searched">
					<a href="javascript:void(0);" class="gray_bt" @click="print()">Print</a>
					<a href="javascript:void(0);" class="gray_bt" @click="excel()">Excel</a>
				</span>
				<em class="lt_text fl_r">
					<b>Total: {{ totalAmount | toCurrency }} ({{ list.length }})</b>
				</em>
			</div>
		</div>

		<div class="fixed-table-container-none-padding">
			<div class="table-wrapper" style="height: 90%;">
				<table class="list_tbl" id="deliveryList">
					<thead>
						<tr>
							<th width="3%">#</th>
							<th width="12%">Name Of Acc.</th>
							<th>Address</th>
							<th width="7%">City</th>
							<th width="7%">Sales Rep</th>
							<th width="7%">Term</th>
							<th width="7%">Inv#</th>
							<th width="7%">Amt</th>
							<th width="8%">Memo</th>
							<th width="4%">Box</th>
							<th width="4%">LB</th>
							<th width="4%">Cash</th>
							<th width="5%">COD Ck</th>
							<th width="4%">P.O.C</th>
							<th width="5%">Check#</th>
						</tr>
					</thead>
					<tbody v-for="(group, gi) in groupedList" :key="gi">
						<tr v-if="group_by_route" class="route-row">
							<td colspan="15" class="al_left">
								<b>Route: {{ group.route || '(None)' }}</b> &nbsp;({{ group.items.length }} / {{ group.amount | toCurrency }})
							</td>
						</tr>
						<tr v-for="row in group.items" :key="row.invoice_no">
							<td class="al_center">{{ row.seq }}</td>
							<td class="al_left truncate">{{ row.ship_to_name }}</td>
							<td class="al_left">{{ row.ship_to_address1 }}</td>
							<td class="al_left">{{ row.address_city }}</td>
							<td class="al_left">{{ row.sales_rep_name }}</td>
							<td class="al_left">{{ row.payment_term_name }}</td>
							<td class="al_center">{{ row.invoice_dn }}</td>
							<td class="al_right">{{ row.invoice_amount | toCurrency }}</td>
							<td class="al_left">{{ row.memo }}</td>
							<td class="al_center">{{ boxText(row) }}</td>
							<td class="al_right">{{ lbText(row) }}</td>
							<td></td>
							<td></td>
							<td></td>
							<td></td>
						</tr>
					</tbody>
					<tbody v-if="searched && list.length">
						<tr class="total-row">
							<td colspan="10" class="al_right"><b>Total LB</b></td>
							<td class="al_right">
								<b>{{ totalLbText }}</b>
							</td>
							<td colspan="4"></td>
						</tr>
						<tr v-if="manualMissingCount" class="note-row">
							<td colspan="15" class="al_left">{{ weightNote }}</td>
						</tr>
					</tbody>
					<tbody v-if="searched && !list.length">
						<tr>
							<td colspan="15" class="al_center">No data</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script>
import CustomDatePicker from '@/components/common/CustomDatePicker.vue';
import { today } from '@/utils/date';
import { apiDeliveryList } from '@/api/report/report';
import { apiInvoiceIndex } from '@/api/sales/invoice.js';
import { exportExcel } from '@/api/util';
import { toCurrency } from '@/utils/etc.js';

export default {
	components: {
		CustomDatePicker,
	},
	data: () => ({
		from_date: today(),
		to_date: today(),
		delivery_type_code: '',
		group_by_route: false,
		deliveryTypes: [],
		list: [],
		company_id: null,
		searched: false,
	}),
	computed: {
		totalAmount() {
			return this.list.reduce((sum, r) => sum + (Number(r.invoice_amount) || 0), 0);
		},
		// #3150 총중량 합계. 수동 확정값이 없는 건(=자동계산값)도 포함되므로 별표로 표시한다.
		totalLb() {
			return this.list.reduce((sum, r) => sum + (Number(r.total_lb) || 0), 0);
		},
		manualMissingCount() {
			return this.list.filter(r => !this.isManualWeight(r)).length;
		},
		totalLbText() {
			const text = toCurrency(this.totalLb);
			return this.manualMissingCount ? text + '*' : text;
		},
		weightNote() {
			return `* Includes ${this.manualMissingCount} invoice(s) without Manual Total LB`;
		},
		groupedList() {
			const groups = [];
			if (!this.group_by_route) {
				groups.push({ route: null, items: this.list, amount: this.totalAmount });
			} else {
				for (const row of this.list) {
					const key = row.zone_code || '';
					let g = groups.find(x => x.route === key);
					if (!g) {
						g = { route: key, items: [], amount: 0 };
						groups.push(g);
					}
					g.items.push(row);
					g.amount += Number(row.invoice_amount) || 0;
				}
			}
			// #3150 '#' 번호는 그룹 재배치가 끝난 최종 표시 순서에서 매긴다. 원본 row 는 건드리지 않고 복사본에 담는다.
			// (화면·print()·Excel 이 모두 이 groupedList 를 타므로 여기 한 곳에서만 부여하면 세 경로가 함께 맞는다.
			//  템플릿 v-for 인덱스로 번호를 매기면 row.seq 를 읽는 print() 만 조용히 어긋난다.)
			let seq = 0;
			for (const g of groups) {
				g.items = g.items.map(row => ({ ...row, seq: ++seq }));
			}
			return groups;
		},
	},
	methods: {
		// #3150 Box/LB 표시 규칙 (화면·인쇄 공용 헬퍼).
		// box_count·total_lb 는 값이 NULL 이면 JSON 키 자체가 없어 undefined 로 도착한다(MyBatis callSettersOnNulls=false).
		// 따라서 null 비교가 아니라 "유한한 양수일 때만 표시" 로 판정한다.
		isManualWeight(row) {
			// manual_total_weight 는 서버에서 NULL 또는 1 이상 정수만 저장된다 (InvoiceMapper.xml)
			return isFinite(Number(row.manual_total_weight)) && Number(row.manual_total_weight) > 0;
		},
		boxText(row) {
			const v = Number(row.box_count);
			return isFinite(v) && v > 0 ? String(v) : '';
		},
		// 수동 확정값이 없으면 자동계산값(팔레트 무게뿐일 수 있음)이므로 별표를 붙여 미확정임을 알린다.
		// 값이 없거나 0 이면 '0.00 LB' 라는 허위값 대신 '—' 를 찍는다.
		lbText(row) {
			const v = Number(row.total_lb);
			const text = isFinite(v) && v > 0 ? toCurrency(v) : '—';
			return this.isManualWeight(row) ? text : text + '*';
		},
		async search() {
			if (!(this.from_date && this.to_date)) return this.$toast.error('Please, Select Ship/PickUp Date');
			const { data } = await apiDeliveryList(this.from_date, this.to_date, this.delivery_type_code || '');
			this.list = data.list || [];
			this.company_id = data.company_id || null;
			this.searched = true;
		},
		reset() {
			this.from_date = today();
			this.to_date = today();
			this.delivery_type_code = '';
			this.group_by_route = false;
			this.list = [];
			this.searched = false;
		},
		escape(v) {
			if (v === null || v === undefined) return '';
			return String(v)
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;');
		},
		// 기사 기입칸(Box/Cash/COD Ck/P.O.C/Check#) 포함 배송 명단을 전용 인쇄 창으로 출력
		print() {
			if (!this.list.length) return this.$toast.error('No List To Print');
			const esc = this.escape;
			const dt = this.deliveryTypes.find(d => d.code === this.delivery_type_code);
			const dtName = dt ? dt.name : '';
			const headers = ['#', 'Name Of Acc.', 'Address', 'City', 'Sales Rep', 'Term', 'Inv#', 'Amt', 'Memo', 'Box', 'LB', 'Cash', 'COD Ck', 'P.O.C', 'Check#'];
			let body = '';
			for (const group of this.groupedList) {
				if (this.group_by_route) {
					body += `<tr class="route"><td colspan="${headers.length}"><b>Route: ${esc(group.route || '(None)')}</b> (${group.items.length} / ${toCurrency(group.amount)})</td></tr>`;
				}
				for (const row of group.items) {
					// Box/LB 는 화면과 동일한 헬퍼를 쓴다. 문자열 보간이라 값이 없을 때 'undefined' 가 그대로 인쇄되지 않도록 반드시 헬퍼를 거친다.
					body +=
						`<tr><td class="c">${row.seq}</td><td>${esc(row.ship_to_name)}</td><td>${esc(row.ship_to_address1)}</td>` +
						`<td>${esc(row.address_city)}</td><td>${esc(row.sales_rep_name)}</td><td>${esc(row.payment_term_name)}</td>` +
						`<td class="c">${esc(row.invoice_dn)}</td><td class="r">${toCurrency(row.invoice_amount)}</td><td>${esc(row.memo)}</td>` +
						`<td class="c">${esc(this.boxText(row))}</td><td class="r">${esc(this.lbText(row))}</td>` +
						`<td></td><td></td><td></td><td></td></tr>`;
				}
			}
			body += `<tr class="total"><td class="r" colspan="10"><b>Total LB</b></td><td class="r"><b>${esc(this.totalLbText)}</b></td><td colspan="4"></td></tr>`;
			if (this.manualMissingCount) body += `<tr class="note"><td colspan="${headers.length}">${esc(this.weightNote)}</td></tr>`;
			const head = headers.map(h => `<th>${h}</th>`).join('');
			const meta = `Ship/PickUp Date : ${this.from_date} ~ ${this.to_date}` + (dtName ? ` &nbsp;&nbsp; Delivery Type : ${esc(dtName)}` : '') + ` &nbsp;&nbsp; Total : ${toCurrency(this.totalAmount)} (${this.list.length})`;
			const html =
				`<!DOCTYPE html><html><head><title>Delivery List</title><style>` +
				`@page{size:landscape;margin:8mm;}` + // 15컬럼이라 세로로는 Address/Memo 가 뭉개진다
				`body{font-family:Arial,sans-serif;margin:12px;}h2{margin:0 0 4px;}.meta{font-size:12px;margin-bottom:8px;}` +
				`table{border-collapse:collapse;width:100%;font-size:11px;}th,td{border:1px solid #333;padding:3px 5px;}` +
				`th{background:#eee;}td.c{text-align:center;}td.r{text-align:right;}tr.route td{background:#e8eef5;font-size:12px;}` +
				`tr.total td{background:#f2f2f2;}tr.note td{border:none;text-align:left;font-size:10px;}` +
				`</style></head><body><h2>DELIVERY LIST</h2><div class="meta">${meta}</div>` +
				`<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
			const w = window.open('', '_blank');
			if (!w) return this.$toast.error('Popup blocked. Please allow popups to print.');
			w.document.write(html);
			w.document.close();
			w.focus();
			setTimeout(() => w.print(), 300);
		},
		excel() {
			if (!this.list.length) return this.$toast.error('No List To Export');
			exportExcel('deliveryList', 'DeliveryList');
		},
		async index() {
			const { data } = await apiInvoiceIndex();
			this.deliveryTypes = data.deliveryTypeList || [];
		},
	},
	created() {
		this.$store.commit('setSideIndex', 7);
		this.$store.commit('setSelectedMenu', 'nav_li_deliveryList');
		this.$store.commit('setSubTitle', 'Report -> Delivery List');
		this.index();
	},
};
</script>

<style scoped>
.route-row td {
	background: #eef3f8;
	font-size: 13px;
}
/* zebra 스타일이 tr 배경을 덮으므로 배경색은 td 레벨에 준다 */
.total-row td {
	background: #f2f2f2;
	font-size: 13px;
}
.note-row td {
	background: #fff;
	border: none;
	color: #d9534f;
}
</style>
